<template>
    <div>
        <ts-page-title
            :title="$t('productProfile.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('productProfile.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper>
                <div class="tw-flex tw-justify-between tw-items-center">
                    <div>
                        <a-button
                            v-if="$can('create-product-profiles')"
                            type="primary"
                            @click="addNew"
                        >
                            {{ $t("addNew") }}
                        </a-button>
                    </div>
                    <div class="tw-space-x-3">
                        <a-tooltip :title="$t('productProfile.search')">
                            <a-input-search
                                v-model="search"
                                :placeholder="$t('productProfile.search')"
                                style="width: 200px"
                            />
                        </a-tooltip>
                    </div>
                </div>
            </ts-panel-wrapper>
            <div class="tw-overflow-x-scroll">
                <ts-table
                    :columns="columns"
                    :records="resources"
                    :loading="loading"
                >
                    <template v-slot="{ record }">
                        <td class="tw-w-16">
                            <img
                                :src="record.photo ? record.photo : defaultImg"
                                alt=""
                                class="tw-w-16 tw-h-8 tw-object-contain"
                                @error="handleImageError"
                            />
                        </td>
                        <td
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.shop_name }}
                        </td>
                        <td
                            valign="middle"
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.original_code }}
                        </td>
                        <td
                            valign="middle"
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.barcode }}
                        </td>
                        <td
                            valign="middle"
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.product_name_kh }}
                        </td>
                        <td
                            valign="middle"
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.product_name_en }}
                        </td>
                        <td
                            valign="middle"
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.category_name_en }}
                        </td>
                        <td
                            valign="middle"
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.currency_code }}
                        </td>
                        <td valign="middle" class="text-left tw-space-x-3">
                            <template v-if="!record.is_deleted">
                                <a
                                    v-if="$can('update-product-profiles')"
                                    href="#"
                                    v-tooltip="$t('edit')"
                                    class="text-primary mr-2"
                                    @click.prevent="edit(record)"
                                >
                                    <i class="fas fa-edit"></i>
                                </a>
                                <a-popconfirm
                                    v-if="$can('delete-product-profiles')"
                                    placement="leftTop"
                                    :title="$t('are_you_sure_to_delete')"
                                    :ok-text="$t('yes')"
                                    :cancel-text="$t('no')"
                                    @confirm="deleteRecord(record)"
                                >
                                    <a href="#" :disabled="record._deleting">
                                        <i
                                            class="fas fa-circle-notch spin"
                                            v-if="record._deleting"
                                        ></i>
                                        <i
                                            class="fa fa-trash text-red"
                                            v-else
                                        ></i>
                                    </a>
                                </a-popconfirm>
                            </template>
                        </td>
                    </template>
                </ts-table>
            </div>
            <div class="d-flex justify-content-end tw-p-4">
                <ts-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-pagination>
            </div>
        </ts-panel>

        <!-- form-action -->
        <a-modal
            v-if="showForm"
            v-model="showForm"
            :title="$t('productProfile.pageTitle')"
            centered
            :maskClosable="false"
            @cancel="clearEdit"
            :zIndex="1020"
            :footer="null"
        >
            <FormAction @cancel="clearEdit" />
        </a-modal>
    </div>
</template>

<script>
import { debounce } from "lodash";
import FormAction from "./form";
import { mapState } from "vuex";

export default {
    name: "productProfile",
    components: {
        FormAction
    },
    data() {
        return {
            defaultImg: require("@/assets/default-image.jpg"),
            loading: false,
            showForm: false
        };
    },
    computed: {
        ...mapState("inventory/productProfile", ["resources", "pagination"]),
        search: {
            get() {
                return this.$store.state.inventory.productProfile.search;
            },
            set(newValue) {
                this.$store.commit(
                    "inventory/productProfile/SET_SEARCH",
                    newValue
                );
                this.$store.commit(
                    "inventory/productProfile/RESET_CURRENT_PAGE"
                );
            }
        },
        columns() {
            return [
                {
                    name: this.$t("productProfile.photo"),
                    style: "width:fit-content"
                },
                { name: this.$t("productProfile.shopName"), sortKey: 'shop_name' },
                { name: this.$t("productProfile.originCode"), sortKey: 'original_code' },
                { name: this.$t("productProfile.barcode"), sortKey: 'barcode' },
                { name: this.$t("productProfile.nameKh"), sortKey: 'product_name_kh' },
                { name: this.$t("productProfile.nameEn"), sortKey: 'product_name_en' },
                { name: this.$t("productProfile.productCategory"), sortKey: 'category_name_en' },
                { name: this.$t("productProfile.currency"), sortKey: 'currency_code' },
                {
                    name: this.$t("actions"),
                    style: "text-align:left; width: 1px;"
                }
            ];
        }
    },
    methods: {
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("inventory/productProfile/fetch", { ...attributes })
                .then(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.$notify({ type: "error", text: error.message });
                });
        },
        addNew() {
            this.showForm = true;
        },
        clearEdit() {
            this.$store.commit("inventory/productProfile/SET_EDIT_DATA", {});
            this.fetchData();
            this.showForm = false;
        },
        edit(record) {
            this.$store.commit(
                "inventory/productProfile/SET_EDIT_DATA",
                record
            );
            this.showForm = true;
        },
        deleteRecord(record) {
            record._deleting = true;
            this.$store
                .dispatch("inventory/productProfile/destroy", record.product_id)
                .then(response => {
                    this.$notify({ type: "success", text: response.message });
                    this.fetchData();
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                })
                .finally(() => {
                    record._deleting = false;
                });
        },
        handleImageError() {
            
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData();
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit("inventory/productProfile/RESET_STATE");
        next();
    }
};
</script>
